import React, { useState } from 'react';
import axios from 'axios';
import { OrderHeader } from './orderdriver/OrderHeader';
import { OrderMap } from './orderdriver/OrderMap';
import { OrderActions } from './orderdriver/OrderActions';
import { SubmitButton } from './orderdriver/SubmitButton';
import { NoteModal } from './orderdriver/NoteModal';
import { StatusModal } from './orderdriver/StatusModal';
import CameraModal from './orderdriver/CameraModal';
import { InfoModal } from './orderdriver/InfoModal';
import { ContactModal } from './orderdriver/ContactModal';
import { useDriverLocation } from './orderdriver/hooks/useDriverLocation';
import { formatArrivalTime } from './orderdriver/utils/timeUtils';

function OrderDriver({ order, onBack, onBackClick }) {
  const [note, setNote] = useState('');
  const [images, setImages] = useState([]);
  
  const getFilteredStatus = (orderStatus) => {
  const validStatuses = ['recogido', 'cancelado'];
    return validStatuses.includes(orderStatus?.toLowerCase()) ? orderStatus : '';
  };

const [status, setStatus] = useState(getFilteredStatus(order?.status));
  
  // Modal states
  const [openNoteModal, setOpenNoteModal] = useState(false);
  const [openStatusModal, setOpenStatusModal] = useState(false);
  const [openCameraModal, setOpenCameraModal] = useState(false);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [openContactModal, setOpenContactModal] = useState(false);
  
  const [loading, setLoading] = useState(false);

  // Custom hook for driver location
  const driver = useDriverLocation(order?.driver_id);

  // Format arrival time
  const formattedArrivalTime = order?.arrival_time 
    ? formatArrivalTime(order.arrival_time)
    : '';

  const handleSubmit = async () => {
    // Validation
    if (!note || !status) {
      alert('Por favor proporcione una nota y seleccione un estado antes de enviar.');
      return;
    }

    if (images.length === 0) {
      alert('Por favor agregue al menos una imagen.');
      return;
    }

    const formData = new FormData();
    formData.append('note', note);
    formData.append('status', status);
    
    images.forEach((image, index) => {
      formData.append('images', image, `image-${index}.jpg`);
    });

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/driver_order_submit/${order.id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      alert('¡Recojo completado exitosamente!');
      onBack();
    } catch (error) {
      console.error('Error submitting order:', error);
      alert('Hubo un error al enviar el recojo. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <OrderHeader
        order={order}
        formattedArrivalTime={formattedArrivalTime}
        onBackClick={onBack}
      />

      {/* Map Container */}
      <div className="flex-1 relative">
        <OrderMap
          order={order}
          driver={driver}
        />

        {/* Action Buttons Overlay */}
        <OrderActions
          order={order}
          hasNote={!!note}
          hasImages={images.length > 0}
          hasStatus={!!status}
          onInfoClick={() => setOpenInfoModal(true)}
          onContactClick={() => setOpenContactModal(true)}
          onDirectionsClick={() => {
            const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${order.latitude},${order.longitude}`;
            window.open(googleMapsUrl, '_blank');
          }}
          onNoteClick={() => setOpenNoteModal(true)}
          onCameraClick={() => setOpenCameraModal(true)}
          onStatusClick={() => setOpenStatusModal(true)}
        />

        {/* Submit Button */}
        <SubmitButton
          loading={loading}
          onClick={handleSubmit}
        />
      </div>

      {/* Modals */}
      <NoteModal
        open={openNoteModal}
        onClose={() => setOpenNoteModal(false)}
        note={note}
        onNoteChange={setNote}
      />

      <StatusModal
        open={openStatusModal}
        onClose={() => setOpenStatusModal(false)}
        status={status}
        onStatusChange={setStatus}
      />

      <CameraModal
        open={openCameraModal}
        onClose={() => setOpenCameraModal(false)}
        images={images}
        onImagesChange={setImages}
      />

      <InfoModal
        open={openInfoModal}
        onClose={() => setOpenInfoModal(false)}
        order={order}
      />

      <ContactModal
        open={openContactModal}
        onClose={() => setOpenContactModal(false)}
        order={order}
      />

      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-3">
            <div className="w-12 h-12 border-4 border-brand-gold border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-700 font-medium">Enviando recojo...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderDriver;